import { BRAND, BRAND_APP } from './brand';

// Icons come exclusively from lucide-react here: react-icons ships each icon
// family as one giant non-tree-shakeable module, and importing from ten
// families pulled ~700 KB of icons into the main bundle.
import {
  Image,
  Proportions,
  Timer,
  Clock,
  ClipboardList,
  History,
  PlayCircle,
  Video,
  Calculator,
  Film,
  Sparkles,
  FileText,
  FileDown,
  ArrowLeftRight,
  BarChart3,
  MessageSquarePlus,
} from 'lucide-react';

// Helper to check if we're in beta environment
export const isInBetaEnvironment = () => {
  // Check if we're on beta.data-calc.com
  if (window.location.hostname === 'beta.data-calc.com') {
    return true;
  }
  
  // For local development, strictly check the environment variable
  if (window.location.hostname === 'localhost') {
    return process.env.REACT_APP_ENVIRONMENT === 'beta';
  }
  
  // For all other cases, check the environment variable
  return process.env.REACT_APP_ENVIRONMENT === 'beta';
};

// Helper to detect desktop-app embedded mode.
// Once activated (via query param or UA), the flag is stored in sessionStorage
// so it persists across in-app navigations that drop the query string.
const EMBED_SESSION_KEY = 'datacalc_embed';

export const isEmbeddedMode = () => {
  if (typeof window === 'undefined') return false;

  if (typeof sessionStorage !== 'undefined') {
    const cached = sessionStorage.getItem(EMBED_SESSION_KEY);
    if (cached === '1') return true;
    if (cached === '0') return false;
  }

  let detected = false;

  const params = new URLSearchParams(window.location.search);
  if (params.has('embed')) {
    const embedValue = (params.get('embed') || '').trim().toLowerCase();
    detected = new Set(['1', 'true', 'desktop']).has(embedValue);
  } else if (typeof navigator !== 'undefined') {
    const ua = (navigator.userAgent || '').toLowerCase();
    detected =
      ua.includes('tauri') ||
      ua.includes('webview') ||
      ua.includes('conform.tools desktop') ||
      /\bwv\b/.test(ua);
  }

  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(EMBED_SESSION_KEY, detected ? '1' : '0');
  }

  return detected;
};

// Helper to determine if "Beta" pill should be shown
export const shouldShowBetaPill = (calculator) => {
  const isBeta = isInBetaEnvironment();
  
  // In production, NEVER show beta pill for hidden features
  if (!isBeta && calculator.hide === true) {
    return false;
  }
  
  // Show Beta pill only if:
  // 1. Feature is marked as beta
  // 2. We're not showing the Coming Soon pill
  // 3. Feature is accessible in current environment
  return calculator.beta === true && 
         !shouldShowComingSoonPill(calculator) && 
         isCalculatorAccessible(calculator);
};

// Helper to determine if "Coming Soon" pill should be shown
export const shouldShowComingSoonPill = (calculator) => {
  const isBeta = isInBetaEnvironment();
  
  // In production, NEVER show coming soon pill for hidden features
  if (!isBeta && calculator.hide === true) {
    return false;
  }
  
  // In beta environment: show Coming Soon if feature is coming soon and not production ready
  if (isBeta) {
    return calculator.comingSoon === true && calculator.production === false;
  }
  
  // In production: show Coming Soon if marked as coming soon
  return calculator.comingSoon === true;
};

// Helper to determine if a calculator should be shown in header navigation
export const shouldShowInHeader = (calculator, currentPath) => {
  const isBeta = isInBetaEnvironment();

  // In production, NEVER show hidden features
  if (!isBeta && calculator.hide === true) {
    return false;
  }

  // Private items: in beta, show normally (with BETA pill). In production, only when on the page.
  if (calculator.private === true && !isBeta) {
    return currentPath && currentPath.startsWith(calculator.path);
  }
  
  // If all flags are false, don't show in any environment
  if (!calculator.production && !calculator.beta && !calculator.comingSoon) {
    return false;
  }

  if (isBeta) {
    // In beta: show if it's marked for production or beta
    return calculator.production === true || calculator.beta === true;
  } else {
    // In production: show if marked for production OR (beta AND coming soon)
    return calculator.production === true || (calculator.beta === true && calculator.comingSoon === true);
  }
};

// Helper to check if a private calculator should be visible in the sidebar
export const shouldShowInSidebar = (calculator, currentPath) => {
  // Private items: in beta, show normally (with BETA pill). In production, only when on the page.
  if (calculator.private === true) {
    if (isInBetaEnvironment()) {
      return true; // In beta, show in sidebar like a normal item
    }
    return currentPath && currentPath.startsWith(calculator.path);
  }
  return true;
};

// Helper to determine if a calculator should be accessible
export const isCalculatorAccessible = (calculator) => {
  const isBeta = isInBetaEnvironment();

  // In production, NEVER allow access to hidden features
  if (!isBeta && calculator.hide === true) {
    return false;
  }
  
  // If all flags are false, don't allow access in any environment
  if (!calculator.production && !calculator.beta && !calculator.comingSoon) {
    return false;
  }

  if (isBeta) {
    // In beta: accessible if marked for production or beta
    return calculator.production === true || calculator.beta === true;
  } else {
    // In production: only accessible if marked for production
    return calculator.production === true;
  }
};

// Helper to get the display name for the header menu
export const getCalculatorHeaderName = (path) => {
  // Match by exact path first, then by startsWith for sub-routes (e.g. /cameraspecs/spec)
  const calculator = Object.values(calculatorConfig).find(calc => calc.path === path) ||
    Object.values(calculatorConfig).find(calc => path?.startsWith(calc.path + '/')) ||
    Object.values(calculatorConfig).find(calc => path?.includes(calc.path.slice(1)));
  
  if (calculator) {
    // Don't show header name for hidden features in production
    if (!isInBetaEnvironment() && calculator.hide === true) {
      return 'Calculator';
    }
    // Add (BETA) suffix for beta features
    return calculator.beta ? `${calculator.headerMenu} (BETA)` : calculator.headerMenu;
  }
  return 'Calculator';
};

// Landing page info (not a calculator entry — used by SEOHead for the homepage)
export const landingPageInfo = {
  path: '/',
  title: 'Professional Video Production Calculators',
  metaTitle: `Video Space Calculator & File Size Estimator | ${BRAND_APP}`,
  metaDescription: 'Free video space calculator and file size estimator for video professionals. Calculate video storage needs, camera recording times, aspect ratios, bitrates, timecode math, transfer times, and more. Built for editors, DITs, colorists, and filmmakers.',
  description: 'Free online calculators built for video professionals.',
  seoKeywords: 'video space calculator, video file size calculator, video storage calculator, how much space does video take, camera storage calculator, video production tools, DIT calculator, video editor tools',
};

// Export calculator configurations
export const calculatorConfig = {
  aspectRatio: {
    production: true,
    beta: false,
    comingSoon: false,
    hide: false,
    path: '/aspect-ratio',
    component: 'AspectRatioCalculator',
    title: 'Aspect Ratio Calculator',
    metaTitle: 'Free Aspect Ratio Calculator | Video Resolution & Pixel Dimension Calculator',
    metaDescription: 'Calculate video aspect ratios, pixel dimensions, and resolutions for any format. Convert between 16:9, 2.39:1, 4:3, and custom ratios. Free online tool for film, TV, social media, and streaming formats.',
    headerMenu: 'Aspect Ratio',
    sideMenu: 'Aspect Ratio',
    description: "Free online aspect ratio calculator. Convert video resolutions, calculate pixel dimensions, and find common aspect ratios for film, TV, and social media formats.",
    ogImage: '/og-aspect-ratio.png',
    seoKeywords: 'aspect ratio calculator, video resolution calculator, pixel dimension calculator, 16:9 calculator, 2.39:1 aspect ratio, 4K resolution, video aspect ratio converter, screen resolution calculator, widescreen calculator, anamorphic aspect ratio',
    icon: <Proportions />,
    secondaryIcon: <Calculator />,
  },
  bitrate: {
    production: true,
    beta: false,
    comingSoon: false,
    path: '/bitrate',
    component: 'BitrateCalculator',
    title: 'Bitrate ↔︎ File Size Calculator',
    metaTitle: 'Video Bitrate Calculator | File Size from Bitrate & Compression Calculator',
    metaDescription: 'Calculate video file sizes from bitrate and duration. Convert between Mbps, Gbps, and file sizes. Estimate storage for streaming, encoding, and professional video workflows. Free bitrate to file size calculator.',
    headerMenu: 'Bitrate ↔︎ File Size',
    sideMenu: 'Bitrate ↔︎ File Size',
    description: "Professional video bitrate calculator. Calculate optimal bitrate settings, estimate file sizes, and determine video quality for streaming and encoding.",
    ogImage: '/og-bitrate.png',
    seoKeywords: 'video bitrate calculator, bitrate to file size, video compression calculator, streaming bitrate calculator, Mbps to file size, video encoding calculator, bitrate estimator, video quality calculator, h264 bitrate, h265 bitrate, streaming quality calculator',
    icon: <BarChart3 />,
    secondaryIcon: <Calculator />,
  },
  camera: {
    production: true,
    beta: false,
    comingSoon: false,
    path: '/camera',
    component: 'CameraToFileSize',
    title: 'Camera ↔︎ File Size Calculator',
    metaTitle: 'Camera Storage Calculator | How Much Space Does Video Take? | Recording Time Calculator',
    metaDescription: 'Calculate how much storage space your camera needs. Estimate video file sizes and recording times for ARRI, RED, Blackmagic, Sony, Canon, DJI, Panasonic, and more. Free camera storage and video space calculator for professional productions.',
    headerMenu: 'Camera ↔︎ File Size',
    sideMenu: 'Camera ↔︎ File Size',
    description: "Professional camera storage calculator. Estimate video file sizes and recording time for popular cinema cameras including ARRI, RED, Blackmagic, Sony, Canon, and more!",
    ogImage: '/og-camera.png',
    seoKeywords: 'camera storage calculator, how much space does video take, video recording time calculator, ARRI storage calculator, RED storage calculator, Blackmagic storage, Sony camera file size, Canon camera storage, camera card capacity, CFexpress recording time, SSD recording time, ARRIRAW file size, BRAW file size, R3D file size, ProRes file size per hour',
    icon: <Video />,
    secondaryIcon: <Calculator />,
  },
  cameraspecs: {
    production: false,
    beta: true,
    comingSoon: false,
    private: true,  // Not on production site. Visible on beta with BETA pill.
    path: '/cameraspecs',
    noindex: true,
    component: 'CameraSpecsApp',
    title: 'Camera Specs Generator',
    metaTitle: 'Camera Specs Generator | TBD Post',
    metaDescription: 'Generate professional camera and edit spec sheets for film and video production. Auto-filled with smart defaults for ARRI, RED, Sony, Canon, and more.',
    headerMenu: 'Camera Specs',
    sideMenu: 'Camera Specs',
    description: "Generate professional camera spec sheets with smart defaults. Select cameras and NLE, and auto-fill codec, color space, proxy settings, and more.",
    icon: <FileDown />,
    secondaryIcon: <FileText />,
  },
  dailies: {
    production: true,
    beta: false,
    comingSoon: false,
    path: '/dailies',
    component: 'DailiesCalculator',
    title: 'Dailies Calculator',
    metaTitle: 'Film Dailies Calculator | Production Storage & Data Management Calculator',
    metaDescription: 'Calculate storage needs for film dailies and video production workflows. Estimate transcode times, RAID storage capacity, shuttle drive requirements, and data management needs. Free dailies calculator for DITs and post-production teams.',
    headerMenu: 'Dailies Calc',
    sideMenu: 'Dailies Calculator',
    description: "Film and video dailies calculator for professional productions. Calculate storage requirements and data management needs for your project's ideal workflow.",
    ogImage: '/og-dailies.png',
    seoKeywords: 'dailies calculator, film dailies storage, production storage calculator, DIT calculator, data management calculator, transcode time calculator, RAID storage calculator, video production storage, dailies workflow, post production storage, shuttle drive calculator',
    icon: <PlayCircle />,
    secondaryIcon: <Calculator />,
  },
  duration: {
    production: true,
    beta: false,
    comingSoon: false,
    path: '/duration',
    component: 'DurationToFileSize',
    title: 'Video Duration ↔︎ File Size Calculator',
    metaTitle: 'Video File Size Calculator | How Big Is My Video? | Duration to Storage Estimator',
    metaDescription: 'Calculate video file sizes from duration, or find how long you can record with available storage. Estimate storage for ProRes, H.264, H.265, DNxHR, and more. Free video space calculator for any codec and resolution.',
    headerMenu: 'Duration ↔︎ File Size',
    sideMenu: 'Duration ↔︎ File Size',
    description: "Video duration and file size calculator. Quickly estimate storage needs, convert between duration and file size, and plan media storage for any video format.",
    ogImage: '/og-duration.png',
    seoKeywords: 'video file size calculator, video duration calculator, how big is my video, video storage estimator, video space calculator, ProRes file size, H.264 file size, H.265 file size, DNxHR file size, video length to file size, storage per hour of video, how much storage for video',
    icon: <Clock />,
    secondaryIcon: <Calculator />,
  },
  // fileName: {
  //   production: false,
  //   beta: false,
  //   comingSoon: false,
  //   hide: true,
  //   path: '/filename-creator',
  //   component: 'FileNameCreator',
  //   title: 'File Name Creator',
  //   metaTitle: 'Professional File Name Generator | Media Naming Tool',
  //   metaDescription: 'Create standardized file names for video production and post-production. Generate consistent naming conventions for dailies, deliverables, and media assets.',
  //   headerMenu: 'File Name Creator',
  //   sideMenu: 'File Name Creator',
  //   description: "Professional file naming tool for media workflows. Create consistent, industry-standard file names for video, dailies, and post-production deliverables.",
  //   icon: <FileText />,
  //   secondaryIcon: <Sparkles />,
  // },
  film: {
    production: true,
    beta: false,
    comingSoon: false,
    path: '/film',
    component: 'FilmCalculator',
    title: 'Motion Picture Film Calculator',
    metaTitle: '16mm & 35mm Film Stock Calculator | Footage & Runtime Calculator',
    metaDescription: 'Calculate film stock requirements for 16mm and 35mm productions. Estimate footage length, runtime, and stock costs. Essential free calculator for cinematographers, film students, and analog film productions.',
    headerMenu: 'Film Calc',
    sideMenu: 'Film Calculator',
    description: "Motion picture film stock calculator. Calculate 16mm and 35mm film footage, runtime, and stock needed. Essential tool for film productions and cinematographers.",
    ogImage: '/og-film.png',
    seoKeywords: '16mm film calculator, 35mm film calculator, film stock calculator, motion picture film, film footage calculator, film runtime calculator, feet of film, film roll calculator, analog film calculator, cinematography calculator, film production calculator',
    icon: <Film />,
    secondaryIcon: <Calculator />,
  },  
  framingChartGenerator: {
    production: true,
    beta: false,
    comingSoon: false,
    path: '/framing-chart',
    component: 'FramingChartGenerator',
    title: 'Framing Chart Generator',
    metaTitle: 'Free Framing Chart Generator | Camera Safe Area & Aspect Ratio Overlay Tool',
    metaDescription: 'Generate professional camera framing charts with safe areas, aspect ratio overlays, and composition guides. Download high-res PDFs for on-set reference. Free framing chart tool for cinematographers and camera departments.',
    headerMenu: 'Framing Chart',
    sideMenu: 'Framing Chart',
    description: "Professional framing chart generator for film and video production. Create precise camera framing guides, safe areas, and aspect ratio overlays for any format.",
    ogImage: '/og-framing-chart.png',
    seoKeywords: 'framing chart generator, camera framing chart, safe area overlay, aspect ratio overlay, camera guide, title safe area, action safe area, framing guide PDF, cinematography framing chart, camera department tools, on-set framing reference',
    icon: <Image />,
    secondaryIcon: <Sparkles />,
  },
  // timecode: {
  //   production: true,
  //   beta: true,
  //   comingSoon: false,
  //   path: '/timecode',
  //   component: 'TimecodeCalculator',
  //   title: 'Timecode Calculator',
  //   metaTitle: 'Professional Timecode Calculator | Frame Rate Converter',
  //   metaDescription: 'Calculate and convert timecode for any frame rate. Add, subtract, and convert between different timecode formats. Essential tool for video professionals.',
  //   headerMenu: 'Timecode Calc',
  //   sideMenu: 'Timecode Calculator',
  //   description: "Professional timecode calculator for film and video. Convert between timecode formats, add/subtract timecode. An essential tool for video professionals",
  //   icon: <Timer />,
  //   secondaryIcon: <Calculator />,
  // },
  timecodecalculator: {
    production: true,
    beta: false,
    comingSoon: false,
    path: '/timecode',
    component: 'TimecodeGraphingCalculator',
    title: 'Timecode Calculator',
    metaTitle: 'Free Timecode Calculator | Add, Subtract & Convert Timecode | Frame Rate Converter',
    metaDescription: 'Add, subtract, multiply, and divide timecode with a graphing calculator style interface. Convert between frame rates (23.976, 24, 25, 29.97, 30). Free timecode calculator with calculation history for editors and post-production.',
    headerMenu: 'Timecode Calc',
    sideMenu: 'Timecode Calculator',
    description: "Professional timecode calculator with a TI-83 style interface. Enter timecode with a numeric keypad, perform calculations, and keep track of your calculation history with a paper tape.",
    ogImage: '/og-timecode.png',
    seoKeywords: 'timecode calculator, timecode converter, add timecode, subtract timecode, frame rate converter, SMPTE timecode calculator, 23.976 timecode, 29.97 drop frame, timecode math, video timecode calculator, post production timecode, editing timecode calculator',
    icon: <Timer />,
    secondaryIcon: <Calculator />,
  },
  transferTime: {
    production: true,
    beta: false,
    comingSoon: false,
    path: '/transfer-time',
    component: 'TransferTimeCalculator',
    title: 'Transfer Time Calculator',
    metaTitle: 'File Transfer Time Calculator | How Long to Copy Video Files? | Data Transfer Estimator',
    metaDescription: 'Calculate how long it takes to transfer video files. Estimate transfer and upload times for USB 3, Thunderbolt, 10GbE, Wi-Fi, internet uploads, and more. Free data transfer time calculator for media professionals, DITs, and editors.',
    headerMenu: 'Transfer Time',
    sideMenu: 'Transfer Time',
    description: "Media transfer time calculator for video professionals. Estimate file transfer times across different storage devices, connections, network speeds, and internet upload speeds.",
    ogImage: '/og-transfer-time.png',
    seoKeywords: 'transfer time calculator, file transfer time, data transfer calculator, how long to copy files, USB transfer speed, Thunderbolt transfer time, 10GbE transfer speed, upload time calculator, internet upload time, media transfer calculator, video file transfer, offload time calculator, DIT transfer time, network transfer calculator',
    icon: <ArrowLeftRight />,
    secondaryIcon: <Timer />,
  },

  request: {
    production: true,
    beta: false,
    comingSoon: false,
    path: '/request',
    component: 'RequestPage',
    title: 'Submit a Request',
    metaTitle: `Submit a Request | ${BRAND_APP}`,
    metaDescription: 'Submit feature requests, bug reports, or general feedback for DataCalc video production calculators.',
    headerMenu: 'Request',
    sideMenu: 'Request',
    description: "Have a feature idea, bug report, or general feedback? Submit a request and let us know how we can improve.",
    icon: <MessageSquarePlus />,
    secondaryIcon: <MessageSquarePlus />,
    excludeFromMobileNav: true,
    excludeFromLandingGrid: true,
  },
  logs: {
    production: true,
    beta: false,
    comingSoon: false,
    path: '/logs',
    noindex: true,
    component: 'Log',
    title: 'Logs',
    metaTitle: 'Video Calculator Logs | Save & Share Calculations',
    metaDescription: 'Track and share your video calculations. Save file sizes, durations, and technical specifications. Collaborate on media projects with shareable results.',
    headerMenu: 'Logs',
    sideMenu: 'Logs',
    description: "Save and share your video calculations. Track file sizes, durations, and technical specifications for your media projects.",
    icon: <History />,
    secondaryIcon: <ClipboardList />,
  },
  // analytics: {
  //   production: false,
  //   beta: false,
  //   comingSoon: false,
  //   hide: true,  // Hide from users - analytics runs in background only
  //   path: '/analytics',
  //   component: 'AnalyticsDashboard',
  //   title: 'Analytics Dashboard',
  //   metaTitle: 'Usage Analytics Dashboard | DataCalc Statistics',
  //   metaDescription: 'View your DataCalc usage statistics, track framing chart downloads, calculation logs, and discover usage patterns. Monitor your video production workflow analytics.',
  //   headerMenu: 'Analytics',
  //   sideMenu: 'Analytics',
  //   description: "View comprehensive usage analytics for your DataCalc activities. Track framing chart downloads, calculation logs, popular cameras, and usage patterns to optimize your video production workflow.",
  //   icon: <BarChart3 />,
  //   secondaryIcon: <Calculator />,
  // }
};

// Add this helper function to features.js
export const getCalculatorName = (path) => {
  const calculator = Object.values(calculatorConfig).find(calc => {
    // Handle both exact matches and path variations
    return calc.path === path || 
           path?.startsWith(calc.path) ||
           path?.includes(calc.path.slice(1)); // handle paths without leading slash
  });
  return calculator ? calculator.title : 'Calculator';
};

// Add this export
export const getBasename = () => {
  // Always return '/' since we're using subdomains instead of paths
  return '/';
};

// Add this helper for route paths
export const getRoutePath = (path) => {
  // Since we're using subdomains, just return the path as-is
  return path;
};

// Helper function to extract features from description
const extractFeatures = (description) => {
  // Split description into sentences and clean them up
  return description
    .split('.')
    .map(sentence => sentence.trim())
    .filter(sentence => sentence.length > 0)
    .map(sentence => {
      // Remove common starter words to make features more concise
      return sentence.replace(/^(Calculate|Estimate|Convert|Create|Generate|Professional|Free online|Track|Save|View)/i, '').trim();
    });
};

// Helper function to get schema markup for a calculator
export const getCalculatorSchema = (calculatorKey) => {
  const calculator = calculatorConfig[calculatorKey];
  if (!calculator) return null;

  // Always the production origin — schema is baked into prerendered HTML,
  // so window.location.origin would leak localhost/beta hosts into it
  const baseUrl = 'https://data-calc.com';
  
  // Extract features from description
  const features = extractFeatures(calculator.description);
  
  // Base schema that's common for all calculators
  const baseSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": calculator.metaTitle.split('|')[0].trim(),
    "headline": calculator.title,
    "description": calculator.metaDescription,
    "url": `${baseUrl}${calculator.path}`,
    "applicationCategory": "Calculator",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": features,
    "author": {
      "@type": "Organization",
      "name": BRAND,
      "url": "https://conform.tools"
    },
    "publisher": {
      "@type": "Organization",
      "name": BRAND,
      "url": "https://conform.tools"
    }
  };

  // Use seoKeywords from calculator config if available, otherwise fall back to switch defaults
  const seoKeywords = calculator.seoKeywords || '';

  // Add specific properties based on calculator type
  switch (calculatorKey) {
    case 'camera':
      return {
        ...baseSchema,
        "keywords": seoKeywords || "camera storage calculator, how much space does video take, video recording time calculator, ARRI storage, RED storage, Blackmagic storage, Sony camera file size, Canon camera storage, CFexpress recording time, ARRIRAW file size, ProRes file size per hour",
        "applicationSubCategory": "Video Production",
        "audience": {
          "@type": "Audience",
          "audienceType": "Video Professionals, DITs, Cinematographers"
        }
      };

    case 'timecode':
      return {
        ...baseSchema,
        "keywords": seoKeywords || "timecode calculator, timecode converter, add timecode, subtract timecode, frame rate converter, SMPTE timecode, 23.976 timecode, 29.97 drop frame, timecode math",
        "applicationSubCategory": "Video Production",
        "audience": {
          "@type": "Audience",
          "audienceType": "Video Editors, Post-Production Professionals"
        }
      };

    case 'timecodecalculator':
      return {
        ...baseSchema,
        "keywords": seoKeywords || "timecode calculator, timecode converter, add timecode, subtract timecode, frame rate converter, SMPTE timecode calculator, 23.976 timecode, 29.97 drop frame, timecode math, editing timecode calculator",
        "applicationSubCategory": "Video Production",
        "audience": {
          "@type": "Audience",
          "audienceType": "Video Editors, Post-Production Professionals"
        }
      };

    case 'film':
      return {
        ...baseSchema,
        "keywords": seoKeywords || "16mm film calculator, 35mm film calculator, film stock calculator, motion picture film, film footage calculator, film runtime calculator, analog film calculator, cinematography calculator",
        "applicationSubCategory": "Film Production",
        "audience": {
          "@type": "Audience",
          "audienceType": "Cinematographers, Film Students, Film Producers"
        }
      };

    case 'aspectRatio':
      return {
        ...baseSchema,
        "keywords": seoKeywords || "aspect ratio calculator, video resolution calculator, pixel dimension calculator, 16:9 calculator, 2.39:1 aspect ratio, 4K resolution, widescreen calculator, anamorphic aspect ratio",
        "applicationSubCategory": "Video Production",
        "audience": {
          "@type": "Audience",
          "audienceType": "Content Creators, Cinematographers, Video Editors"
        }
      };

    case 'duration':
      return {
        ...baseSchema,
        "keywords": seoKeywords || "video file size calculator, video duration calculator, how big is my video, video storage estimator, video space calculator, ProRes file size, H.264 file size, storage per hour of video",
        "applicationSubCategory": "Video Production",
        "audience": {
          "@type": "Audience",
          "audienceType": "Video Professionals, Editors, Producers"
        }
      };

    case 'bitrate':
      return {
        ...baseSchema,
        "keywords": seoKeywords || "video bitrate calculator, bitrate to file size, video compression calculator, streaming bitrate calculator, Mbps to file size, video encoding calculator, h264 bitrate, h265 bitrate",
        "applicationSubCategory": "Video Production",
        "audience": {
          "@type": "Audience",
          "audienceType": "Video Professionals, Streaming Engineers, Editors"
        }
      };

    case 'transferTime':
      return {
        ...baseSchema,
        "keywords": seoKeywords || "transfer time calculator, file transfer time, data transfer calculator, how long to copy files, USB transfer speed, Thunderbolt transfer time, 10GbE transfer speed, DIT transfer time",
        "applicationSubCategory": "Data Transfer",
        "audience": {
          "@type": "Audience",
          "audienceType": "Media Professionals, DITs, Editors"
        }
      };

    case 'dailies':
      return {
        ...baseSchema,
        "keywords": seoKeywords || "dailies calculator, film dailies storage, production storage calculator, DIT calculator, data management calculator, transcode time calculator, RAID storage calculator",
        "applicationSubCategory": "Film Production",
        "audience": {
          "@type": "Audience",
          "audienceType": "Film Production Teams, DITs, Post-Production"
        }
      };

    case 'framingChartGenerator':
      return {
        ...baseSchema,
        "keywords": seoKeywords || "framing chart generator, camera framing chart, safe area overlay, aspect ratio overlay, camera guide, title safe area, action safe area, framing guide PDF, cinematography framing chart",
        "applicationSubCategory": "Video Production",
        "audience": {
          "@type": "Audience",
          "audienceType": "Camera Operators, Cinematographers, Camera Department"
        }
      };

    // Default schema for other calculators
    default:
      return {
        ...baseSchema,
        "keywords": seoKeywords || '',
      };
  }
};

// Helper function to generate script tag content for schema
export const getSchemaScriptContent = (calculatorKey) => {
  const schema = getCalculatorSchema(calculatorKey);
  if (!schema) return '';
  return `<script type="application/ld+json">${JSON.stringify(schema, null, 2)}</script>`;
};

